import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Stethoscope, Phone, Mail, MapPin, Edit, Trash2, Plus } from 'lucide-react';
import AppLogo from '../components/AppLogo';
import AddProviderDialog, { PROVIDER_TYPES } from '../components/medical/AddProviderDialog';
import EditProviderDialog from '../components/medical/EditProviderDialog';

const MedicalProvidersPage = () => {
  const { circleId } = useParams();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addProviderDialogOpen, setAddProviderDialogOpen] = useState(false);
  const [editProvider, setEditProvider] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newProvider, setNewProvider] = useState({
    provider_type: 'primary_care',
    name: '',
    specialty: '',
    phone: '',
    email: '',
    address: '',
    notes: ''
  });

  useEffect(() => {
    fetchProviders();
  }, [circleId]);

  const fetchProviders = async () => {
    try {
      const response = await axios.get(`${API}/circles/${circleId}/medical-providers`);
      setProviders(response.data);
    } catch (error) {
      toast.error('Failed to fetch medical providers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProvider = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...newProvider, circle_id: circleId };
      Object.keys(payload).forEach(k => { if (payload[k] === '') payload[k] = null; });
      await axios.post(`${API}/medical-providers`, payload);
      toast.success('Provider added successfully!');
      setAddProviderDialogOpen(false);
      setNewProvider({
        provider_type: 'primary_care',
        name: '',
        specialty: '',
        phone: '',
        email: '',
        address: '',
        notes: ''
      });
      fetchProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add provider');
    }
  };

  const handleEditProvider = async (e) => {
    e.preventDefault();
    try {
      const updateData = {
        name: editProvider.name,
        specialty: editProvider.specialty,
        phone: editProvider.phone,
        email: editProvider.email,
        address: editProvider.address,
        notes: editProvider.notes
      };
      
      await axios.put(
        `${API}/medical-providers/${editProvider.id}`,
        updateData
      );
      toast.success('Provider updated successfully!');
      setEditDialogOpen(false);
      setEditProvider(null);
      fetchProviders();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update provider');
    }
  };

  const handleDeleteProvider = async (providerId) => {
    if (!window.confirm('Are you sure you want to delete this provider?')) return;
    
    try {
      await axios.delete(`${API}/medical-providers/${providerId}`);
      toast.success('Provider deleted successfully');
      fetchProviders();
    } catch (error) {
      toast.error('Failed to delete provider');
    }
  };

  const getProviderIcon = (type) => {
    const provider = PROVIDER_TYPES.find(p => p.value === type);
    return provider ? provider.icon : '\u2695\uFE0F';
  };

  const getProviderLabel = (type) => {
    const provider = PROVIDER_TYPES.find(p => p.value === type);
    return provider ? provider.label : type;
  };

  const groupedProviders = providers.reduce((acc, provider) => {
    if (!acc[provider.provider_type]) {
      acc[provider.provider_type] = [];
    }
    acc[provider.provider_type].push(provider);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="medical-providers-page">
      {/* Header */}
      <div className="surface-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AppLogo />
              <div>
                <h1 className="text-2xl font-bold text-foreground">Medical Providers</h1>
                <p className="text-sm text-muted-foreground">Healthcare team and contact information</p>
              </div>
            </div>

            <AddProviderDialog
              open={addProviderDialogOpen}
              onOpenChange={setAddProviderDialogOpen}
              provider={newProvider}
              setProvider={setNewProvider}
              onSubmit={handleAddProvider}
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {providers.length > 0 ? (
          <div className="space-y-8">
            {Object.keys(groupedProviders).map((type) => (
              <div key={type}>
                <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-2">
                  <span className="text-2xl">{getProviderIcon(type)}</span>
                  {getProviderLabel(type)}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {groupedProviders[type].map((provider) => (
                    <Card key={provider.id} className="surface-card " data-testid={`provider-${provider.id}`}>
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-start gap-3 flex-1">
                            <div className="w-12 h-12 bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full flex items-center justify-center text-white text-2xl flex-shrink-0">
                              {getProviderIcon(provider.provider_type)}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-semibold text-foreground text-lg">{provider.name}</h3>
                              {provider.specialty && (
                                <p className="text-sm text-muted-foreground">{provider.specialty}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setEditProvider(provider);
                                setEditDialogOpen(true);
                              }}
                              data-testid={`edit-provider-${provider.id}`}
                            >
                              <Edit className="w-4 h-4 text-muted-foreground" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteProvider(provider.id)}
                              data-testid={`delete-provider-${provider.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm">
                          {provider.phone && (
                            <div className="flex items-center gap-2 text-foreground">
                              <Phone className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                              <a href={`tel:${provider.phone}`} className="hover:text-cyan-600">
                                {provider.phone}
                              </a>
                            </div>
                          )}
                          {provider.email && (
                            <div className="flex items-center gap-2 text-foreground">
                              <Mail className="w-4 h-4 text-cyan-600 flex-shrink-0" />
                              <a href={`mailto:${provider.email}`} className="hover:text-cyan-600">
                                {provider.email}
                              </a>
                            </div>
                          )}
                          {provider.address && (
                            <div className="flex items-start gap-2 text-foreground">
                              <MapPin className="w-4 h-4 text-cyan-600 flex-shrink-0 mt-0.5" />
                              <span>{provider.address}</span>
                            </div>
                          )}
                          {provider.notes && (
                            <div className="mt-3 pt-3 border-t border-border">
                              <p className="text-xs text-muted-foreground">{provider.notes}</p>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full p-6 w-24 h-24 mx-auto mb-6 flex items-center justify-center">
              <Stethoscope className="w-12 h-12 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-foreground mb-2">No Medical Providers Yet</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Add your healthcare team so circle members know who to contact
            </p>
            <Button
              onClick={() => setAddProviderDialogOpen(true)}
              className="btn-primary"
              data-testid="add-first-provider-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Your First Provider
            </Button>
          </div>
        )}
      </div>

      <EditProviderDialog
        open={editDialogOpen}
        onOpenChange={setEditDialogOpen}
        provider={editProvider}
        setProvider={setEditProvider}
        onSubmit={handleEditProvider}
      />
    </div>
  );
};

export default MedicalProvidersPage;
